<template>
  <BaseModal
    :title="_T('@Write review')"
    :modal-width="1015"
    @on-close-modal="onCloseModal"
  >
    <MyForm
      id="add-review"
      name="add-review"
      :schema="schema"
      :state="form"
      class="add-review-modal"
      @submit="onAddReview"
    >
      <MyRating v-model="form.rating" name="rating" />
      <div class="add-review-modal__inputs">
        <MyInput
          v-model.trim="form.name"
          name="name"
          caption="@Name"
          autocomplete="given-name"
          class="wide"
        />
        <MyInput
          v-model.trim="form.email"
          name="email"
          type="email"
          autocomplete="email"
          caption="E-mail"
          class="wide"
        />
        <MyInput
          v-model.trim="form.pros"
          name="pros"
          caption="@Pros"
          :required="false"
          class="wide"
        />
        <MyInput
          v-model.trim="form.cons"
          name="cons"
          caption="@Cons"
          :required="false"
          class="wide"
        />
      </div>
      <MyTextarea
        v-model="form.content"
        name="content"
        caption="@Your comment"
        class="wide"
      />
      <MyInput
        v-model="form.youtubeVideoId"
        name="youtubeVideoId"
        caption="@Add your YouTube video link"
        :required="false"
        class="wide"
      />
      <MyFileUpload
        v-model="form.attachments"
        name="attachments"
        :file-types="attachmentPolicy.mimeTypes"
        :max-size="attachmentPolicy.maxSize"
      />
      <MyCheckbox v-model="form.isReplyTracked" name="isReplyTracked">
        <span>
          {{ _T("@Product notify me of replies by email") }}
        </span>
      </MyCheckbox>
      <div class="add-review-modal__checkbox-wrp">
        <ButtonPrimary
          type="submit"
          :aria-label="_T('@Post a review')"
          :disabled="isHandled"
        >
          {{ _T("@Post a review") }}
        </ButtonPrimary>
        <MyCheckbox
          v-model="form.isAgreementAccepted"
          name="isAgreementAccepted"
        >
          <span>
            {{ _T("@I agree with") }}
            <MyLink name="reviews-policy" target="_blank">
              {{ _T("@Reviews policy") }}
            </MyLink>
          </span>
        </MyCheckbox>
      </div>
    </MyForm>
  </BaseModal>
</template>

<script setup>
import {
  addReviewModal,
  successfulModal,
  useModalStore,
} from "~/modules/shared/modals/store/modalStore";
import { useUserStore } from "~/store/userStore";
import { useSingletonHandler } from "~/uses/useSingletonHandler";
import { useStrapiApi } from "~/uses/useMyFetch";
import ButtonPrimary from "~/modules/shared/buttons/ButtonPrimary.vue";
import BaseModal from "~/modules/shared/modals/BaseModal.vue";
import { reviewAttachmentPolicy as attachmentPolicy } from "~/config/attachment.config";
import { isYoutubeUrl, getYoutubeVideoId } from "~/utils/strings.utils.js";
import { z } from "zod";
import MyForm from "~/nuxt_modules/common/ui/components/form/MyForm.vue";
import MyInput from "~/nuxt_modules/common/ui/components/form/MyInput.vue";
import MyTextarea from "~/nuxt_modules/common/ui/components/form/MyTextarea.vue";
import MyCheckbox from "~/nuxt_modules/common/ui/components/form/MyCheckbox.vue";
import MyFileUpload from "~/nuxt_modules/common/ui/components/form/MyFileUpload.vue";
import MyRating from "~/nuxt_modules/common/ui/components/form/MyRating.vue";

const modalStore = useModalStore();
const userStore = useUserStore();

const { currentUser: user, userFullName } = storeToRefs(userStore);
const product = storeToRefs(modalStore).getExtra.value(addReviewModal);

onMounted(() => {
  if (null !== user.value) {
    form.name = userFullName.value;
    form.email = user.value.email;
  }
});

const successfulData = {
  title: _T("@Successful modal title"),
  description: _T("@Review sent successfully"),
  isWithTimeout: true,
};

const fileSchema = z
  .file()
  .max(attachmentPolicy.maxSize, _T("@File size error"))
  .mime(attachmentPolicy.mimeTypes, _T("@File type error"));

const schema = z.object({
  name: z
    .string(_T("@Entry field required"))
    .max(255, `${_T("@Maximum field length")} 255`),
  email: z
    .email(_T("@You entered an incorrect email"))
    .max(254, `${_T("@Maximum field length")} 254`),
  pros: z
    .string()
    .min(3, `${_T("@Minimum field length")} 3`)
    .max(2048, `${_T("@Maximum field length")} 512`)
    .optional(),
  cons: z
    .string()
    .min(3, `${_T("@Minimum field length")} 3`)
    .max(2048, `${_T("@Maximum field length")} 512`)
    .optional(),
  content: z
    .string(_T("@Entry field required"))
    .min(3, `${_T("@Minimum field length")} 3`)
    .max(2048, `${_T("@Maximum field length")} 2048`),
  youtubeVideoId: z
    .string()
    .refine(
      (url) => (typeof url === "string" ? isYoutubeUrl(url) : false),
      _T("@Incorrect youTube url format"),
    )
    .transform((url) => getYoutubeVideoId(url))
    .optional(),
  rating: z.number().min(1, _T("@Entry field required")),
  attachments: z
    .array(fileSchema)
    .refine((list) => list.length <= 10, _T("@File quantity error"))
    .optional(),
  isReplyTracked: z.boolean().optional().default(false),
  isAgreementAccepted: z.literal(
    true,
    _T("@You must accept the terms and conditions"),
  ),
});

const form = reactive({
  email: undefined,
  name: undefined,
  pros: undefined,
  cons: undefined,
  content: undefined,
  youtubeVideoId: undefined,
  attachments: undefined,
  isReplyTracked: false,
  rating: 0,
  isAgreementAccepted: true,
});

const { handle: onAddReview, isHandled } = useSingletonHandler(async (data) => {
  const formData = new FormData();

  formData.append(
    "data",
    JSON.stringify({
      userId: user.value?.id,
      productCode: product.code,
      name: data.name,
      email: data.email,
      pros: data.pros,
      cons: data.cons,
      content: data.content,
      rating: data.rating,
      youtubeVideoId: data.youtubeVideoId,
      isReplyTracked: data.isReplyTracked,
    }),
  );

  data.attachments?.forEach((file) => {
    formData.append("attachments", file);
  });

  await useStrapiApi("/product/reviews", {
    method: "POST",
    body: formData,
  }).then(() => {
    onCloseModal();
    modalStore.toggleModal(successfulModal, successfulData);
  });
});

const onCloseModal = () => {
  modalStore.toggleModal(addReviewModal);
};
</script>

<style lang="scss" scoped>
.add-review-modal {
  @include flex-container(column, space-between);
  gap: 24px;

  &__inputs {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));

    gap: 24px;

    @include mobile {
      @include flex-container(column);
      gap: 16px;
    }
  }

  &__checkbox-wrp {
    width: min(100%, 330px);
    align-self: center;

    @include flex-container(column, center, center);
    gap: 24px;

    @include mobile {
      width: 100%;
    }
  }
}
</style>
